import dayjs, { type Dayjs } from 'dayjs'
import { type Dispatch, type SetStateAction, createContext, useContext, useMemo } from 'react'

export type ErrorFilters = 'oos' | 'conflict' | 'missing'
export type SchedulePageFilterValue = {
  datePickerType: 'week' | 'month'
  datePickerValue: Dayjs
  depot: string[]
  viewMode: 'driver' | 'tractor'
  shiftFilter?: 'am' | 'pm'
  errorFilter?: ErrorFilters[]
  nameFilter?: string
  grouping?: 'depot' | 'shift'
  sortType: 'name' | 'depot' | 'shiftStart'
  sortDirection: 'asc' | 'desc'
}
export type SchedulePageFilterSetter = Dispatch<SetStateAction<SchedulePageFilterValue>>

type SchedulePageFilterContext = SchedulePageFilterValue & {
  setFilters: SchedulePageFilterSetter
}

export const defaultSchedulePageFilters: SchedulePageFilterValue = {
  datePickerType: 'week',
  datePickerValue: dayjs(),
  depot: [],
  viewMode: 'driver',
  sortType: 'name',
  sortDirection: 'asc',
}

export const stickyFilterKeys: (keyof SchedulePageFilterValue)[] = [
  'depot',
  'errorFilter',
  'sortDirection',
  'sortType',
  'shiftFilter',
]

export type StickyFilters = Pick<SchedulePageFilterValue, (typeof stickyFilterKeys)[number]>
export const STICKY_FILTER_STORAGE_KEY = 'driver-management::schedule-page-filters'

export const SchedulePageFilterContext = createContext<SchedulePageFilterContext>({
  ...defaultSchedulePageFilters,
  setFilters: () => {},
})

export type NormalizedDateRange = {
  startDate: Dayjs
  endDate: Dayjs
}

const zeroTime = (value: Dayjs) => dayjs.tz(value.format('YYYY-MM-DDT00:00:00.000') + 'Z', 'UTC')

/**
 * @returns a normalized date range starting at the beginning of the range type (week/month)
 */
export const getScheduleDateRange = (value: Dayjs, rangeType: 'week' | 'month') => {
  const date = value.startOf('day')
  return rangeType === 'week'
    ? {
        startDate: zeroTime(date.day(0)),
        endDate: zeroTime(date.day(0).add(7, 'days')),
      }
    : {
        startDate: zeroTime(date.date(1)),
        endDate: zeroTime(date.date(1).add(1, 'months')),
      }
}

export const useSchedulePageFilters = () => {
  const ctx = useContext(SchedulePageFilterContext)
  const dateRange = useMemo(
    () => getScheduleDateRange(ctx.datePickerValue, ctx.datePickerType),
    [ctx.datePickerValue, ctx.datePickerType]
  )
  return { ...ctx, dateRange }
}
